import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';
import { getUserInfo } from 'src/features/user/userState';
import { useAppSelector } from "src/hooks/hooks";
import { getReplacementOverlapRateReport } from 'src/features/replacementOrder/replacementApi';
import { Box, Card, CardContent, Container, Typography } from '@mui/material';
import DateRangePicker from 'src/components/date-picker/DateRangePicker';
import { incrementAwaitingApiCounter, decrementAwaitingApiCounter } from 'src/features/ui/uiState';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';


export default function ReplacementOverlapPieChart() {
  const userInfo = useAppSelector(getUserInfo);
  const token = userInfo?.accessToken;
  const [series, setSeries] = useState([0, 0, 0, 0]);
  const [labels, setLabels] = useState(['Both Shipped', 'Only Replacement Shipped', 'Only Parent Shipped', 'Neither Shipped']);
  const [total, setTotal] = useState(0);
  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day').format('MM/DD/YYYY'));
  const [endDate, setEndDate] = useState(dayjs().format('MM/DD/YYYY'));
  const dispatch = useDispatch();

  const onDateChange = (dates) => {
    if (dates != null) {
      const formattedStartDate = dayjs(dates[0]).format('MM/DD/YYYY');
      const formattedEndDate = dayjs(dates[1]).format('MM/DD/YYYY');
      setStartDate(formattedStartDate);
      setEndDate(formattedEndDate);
    }
  };


  const fetchData = async () => {
    dispatch(incrementAwaitingApiCounter());
    try {
      let params = {
        dateRange: `${startDate}-${endDate}`,
      };
      const response = await getReplacementOverlapRateReport(params, token);
      const { total_replacement_orders, times_both_shipped, neither_shipped, only_replacement_shipped, only_parent_shipped } = response?.chart_data;
      const overlapRate = ((times_both_shipped / total_replacement_orders) * 100) || 0;
      setSeries([times_both_shipped, only_replacement_shipped, only_parent_shipped, neither_shipped]);
      setLabels([
        `Both Shipped (${overlapRate.toFixed(2)}%)`,
        `Only Replacement Shipped (${parseFloat(((only_replacement_shipped / total_replacement_orders) * 100) || 0).toFixed(2)}%)`,
        `Only Parent Shipped (${parseFloat(((only_parent_shipped / total_replacement_orders) * 100) || 0).toFixed(2)}%)`,
        `Neither Shipped (${parseFloat(((neither_shipped / total_replacement_orders) * 100) || 0).toFixed(2)}%)`
      ]);
      setTotal(total_replacement_orders);
      dispatch(decrementAwaitingApiCounter());
    } catch (error) {
      console.error('Error fetching data:', error);
      dispatch(decrementAwaitingApiCounter());
    }
  };


  useEffect(() => {
    fetchData();
  }, [endDate]);

  const options = {
    chart: {
      type: 'pie',
      height: 350,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      }
    },
    labels: labels,
    colors: ['#e65100', '#1976d2', '#43a047', '#78909c'], // Dark orange, Dark blue, Green, Gray-blue
    legend: {
      position: 'bottom',
      fontSize: '14px',
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '14px',
        fontWeight: 600,
        colors: ['#fff'], // White text for contrast,

      },
      formatter: (val, opts) => `${opts.w.config.series[opts.seriesIndex]} orders`,
      dropShadow: {
        enabled: true,
        top: 1,
        left: 1,
        blur: 1,
        opacity: 0.8,
      },
    },
    tooltip: {
      y: {
        formatter: (val) => `${val} orders`,
      },
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: { width: 300 },
        legend: { position: 'bottom' },
      },
    }],
  };

  return (
    <>
      <Helmet>
        <title> Overlap Rate | Shipping Insights </title>
      </Helmet>
      <Container>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '1.5rem',
            marginBottom: '1rem',
          }}

        >
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            label='Label Date'
            onDateChange={onDateChange}
          />
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 450 }, my: 2 }}>
          <Card sx={{ boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: 'black' }}>
                Replacement Overlap Rate
              </Typography>
              <Chart
                options={options}
                series={series}
                type="pie"
                width="103%"
              />
              <Typography sx={{ mt: 2, fontSize: '16px', color: 'black', textAlign: 'center', fontWeight: 600 }}>
                Total Replacement Orders:{' '}
                <span style={{ color: 'black', fontWeight: 600 }}>{total}</span>
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </>
  );
};